import { computed, ref, shallowRef, watch, type Ref } from 'vue'
import { useRoute, useRouter, type LocationQuery } from 'vue-router'
import { debounce } from 'lodash-es'
import type { PaginationRequest } from '@/types/http/composition/PaginationRequest.ts'
import type { PaginatedResponse } from '@/types/http/composition/PaginatedResponse.ts'

export type PaginationRequest = {
  per_page?: number
  page?: number
  q?: string
}

export type Meta = {
  current_page: number
  from: number
  last_page: number
  per_page: number
  to: number
  total: number
}

export type PaginatedResponse<T, M = Meta> = {
  data: T[]
  meta: M
}

const DEFAULT_PER_PAGE = 15
const MAX_PER_PAGE = 100

type UseIndexRequestOptions = {}

/* eslint-disable @typescript-eslint/no-explicit-any */
export const useIndexRequest = <F extends (params: any) => Promise<PaginatedResponse<any, any>>>(
  index: F,
  defaults: Partial<Parameters<F>[0]> = {},
  options: UseIndexRequestOptions = {}
) => {
  type Request = Parameters<F>[0] & PaginationRequest
  type T = Awaited<ReturnType<F>>['data'][number]
  type Meta = Awaited<ReturnType<F>>['meta']

  type Params = {
    page: number
    per_page: number
    q: string | null
    sort: string | null
    sort_direction: 'asc' | 'desc' | null
  }

  const _params = ref<Params>({
    page: (defaults.page as number) ?? 1,
    per_page: (defaults.per_page as number) ?? DEFAULT_PER_PAGE,
    q: (defaults.q as string | null) ?? null,
    sort: (defaults.sort as string | null) ?? null,
    sort_direction: (defaults.sort_direction as 'asc' | 'desc' | null) ?? null,
  })

  const _filters = shallowRef<Record<string, unknown>>({})
  const _loading = ref(false)
  const _meta = ref<Meta | null>(null)
  const _items = ref<T[]>([])

  // Readonly computeds
  const page = computed(() => _params.value.page)
  const perPage = computed(() => _params.value.per_page)
  const query = computed(() => _params.value.q)
  const sortBy = computed(() => _params.value.sort)
  const sortDirection = computed(() => _params.value.sort_direction)

  const items = computed(() => _items.value)
  const loading = computed(() => _loading.value)
  const meta = computed(() => _meta.value)
  const lastPage = computed(() => _meta.value?.last_page || 1)
  const totalItems = computed(() => _meta.value?.total || 0)
  const hasSearchQuery = computed(() => String(query.value).length > 0)
  const pages = computed(() => Array.from({ length: lastPage.value }, (_, i) => i + 1))

  const setPage = (number: number) => {
    _params.value.page = Math.max(
      1,
      _meta.value?.last_page ? Math.min(number, _meta.value.last_page) : number
    )
  }

  const nextPage = () => {
    setPage(page.value + 1)
  }

  const previousPage = () => {
    setPage(page.value - 1)
  }

  const setPerPage = (number: number) => {
    _params.value.per_page = Math.max(1, Math.min(number, MAX_PER_PAGE))
    _params.value.page = 1
  }

  const setQuery = (q: string | number | bigint | null | undefined) => {
    _params.value.q = q ? String(q) : null
  }

  const setSort = (key: string | null, direction: 'asc' | 'desc' = 'asc') => {
    _params.value.sort = key
    _params.value.sort_direction = key ? direction : null
  }

  const clearSort = () => {
    _params.value.sort = null
    _params.value.sort_direction = null
  }

  const addFilter = <K extends string, V>(key: K, value: V | null = null) => {
    const filter = ref<V | null>(value)

    watch(
      filter,
      (val) => {
        const next = { ..._filters.value }
        if (val == null || val === '') {
          delete next[key]
        } else next[key] = val
        _filters.value = next
      },
      { immediate: true }
    )

    return filter
  }

  const buildApiParams = (): Request => ({
    ...defaults,
    page: _params.value.page,
    per_page: _params.value.per_page,
    ...(_params.value.q ? { q: _params.value.q } : {}),
    ...(_params.value.sort ? { sort: _params.value.sort } : {}),
    ...(_params.value.sort_direction ? { sort_direction: _params.value.sort_direction } : {}),
    ..._filters.value,
  })

  const handleFetch = async () => {
    const params = buildApiParams()
    _loading.value = true
    const res = await index(params)
    _meta.value = res.meta
    _loading.value = false
    _items.value = res.data
  }

  // Watch params and filters for changes, with immediate boot fetch
  watch(
    () => [_params.value, _filters.value],
    debounce(async () => {
      await handleFetch()
    }, 300),
    { deep: true, immediate: true }
  )

  const params = computed(() => _params.value)

  return {
    items,
    loading,
    page,
    perPage,
    lastPage,
    query,
    meta,
    hasSearchQuery,
    totalItems,
    sortBy,
    sortDirection,
    pages,
    params,
    nextPage,
    previousPage,
    setPage,
    setPerPage,
    setQuery,
    handleFetch,
    addFilter,
    setSort,
    clearSort,
    __T: undefined as unknown as T,
  }
}

export const withUrlSync = <H extends () => any>(handler: H): ReturnType<H> => {
  const route = useRoute()
  const router = useRouter()

  const request = handler() as ReturnType<typeof useIndexRequest>

  const { q, page, per_page, sort, sort_direction } = route.query

  // Initialize from URL
  if (q && typeof q === 'string') {
    request.setQuery(q)
  }
  if (page && typeof page === 'string') {
    request.setPage(parseInt(page, 10))
  }
  if (per_page && typeof per_page === 'string') {
    request.setPerPage(parseInt(per_page, 10))
  }
  if (sort && typeof sort === 'string') {
    request.setSort(sort, sort_direction as 'asc' | 'desc')
  }

  // Sync params back to URL when they change
  const buildUrlParams = () => {
    const q: LocationQuery = {}

    if (request.query.value) {
      q.q = request.query.value
    }

    if (request.page.value !== 1) {
      q.page = String(request.page.value)
    }
    if (request.perPage.value !== DEFAULT_PER_PAGE) {
      q.per_page = String(request.perPage.value)
    }

    if (request.sortBy.value) {
      q.sort = request.sortBy.value
    }

    if (request.sortDirection.value) {
      q.sort_direction = request.sortDirection.value
    }

    return q
  }

  watch(
    () => request.params,
    debounce(() => {
      router.replace({ query: buildUrlParams() })
    }, 300),
    { deep: true }
  )

  return request
}

export const withFilters = <H extends () => any, F extends Record<string, any>>(
  indexHandler: H,
  filterDefs: F
): ReturnType<H> & { filters: { [K in keyof F]: Ref<F[K] | null> } } => {
  const indexRequest = indexHandler()

  const filters = {} as { [K in keyof F]: Ref<F[K] | null> }

  for (const [key, value] of Object.entries(filterDefs)) {
    filters[key as keyof F] = indexRequest.addFilter(key, value)
  }

  return {
    ...indexRequest,
    filters,
  }
}

/* eslint-enable @typescript-eslint/no-explicit-any */
